/**
 * A class name is a *selector* into CSS the customer already ships. This
 * library cannot see those rules, so it cannot validate a name against
 * anything — the only useful question is whether the token could damage the
 * document it lands in, and the answer is almost always no: every renderer sets
 * `class` through a DOM property or a framework binding, never by pasting into
 * markup.
 *
 * So the filter is deliberately permissive. Utility frameworks make class names
 * that look like code — `md:hover:text-lg`, `w-1/2`, `text-[#c00]`,
 * `[&>svg]:size-4` — and a tidier-looking allowlist would reject those and be
 * read as a bug.
 */

/** Whitespace separates tokens, so it cannot appear inside one. */
const TOKEN_SEPARATOR = /\s+/;

/**
 * Control characters, and the two brace characters. Braces are not dangerous in
 * a class attribute; they are rejected because a token containing one is nearly
 * always an inline marker the author pasted into the wrong field, and silently
 * accepting it would style nothing and explain nothing.
 */
// eslint-disable-next-line no-control-regex -- control characters are what this matches
const REJECTED_IN_TOKEN = /[\u0000-\u001f\u007f{}]/;

/**
 * Bounds, so a pathological value cannot become a pathological `class`
 * attribute. Both are far past anything a person types by hand.
 */
const MAX_TOKENS = 50;
const MAX_TOKEN_LENGTH = 120;

const isUsableToken = (token: string): boolean =>
  // Splitting a blank string yields one empty token, which is not a class name.
  token !== '' && token.length <= MAX_TOKEN_LENGTH && !REJECTED_IN_TOKEN.test(token);

/**
 * The `class` value for a styling entry, or `undefined` when it has none worth
 * setting.
 *
 * Duplicates collapse and order is the author's. One bad token costs that
 * token: the rest of a mostly-fine list still applies, which is the same
 * per-field tolerance `resolveLinkAttributes` gives a link entry.
 */
export function resolveClassNames(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const tokens = value.trim().split(TOKEN_SEPARATOR).filter(isUsableToken);
  if (tokens.length === 0) {
    return undefined;
  }

  return Array.from(new Set(tokens)).slice(0, MAX_TOKENS).join(' ');
}
